import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/constants';
import { authService } from '@/services/api/auth.service';
import { ApiError } from '@/services/api/client';
import { getAccessToken } from '@/services/api/tokens';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { sessionEnded, sessionStarted, userLoaded } from '@/store/slices';
import type { LoginRequest } from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for authentication.
 *
 * React Query owns fetching, caching and retry; Redux owns the answer to "is
 * someone signed in", which guards and layout read synchronously.
 */

export function useCurrentUser() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: async ({ signal }) => {
      const user = await authService.me(signal);
      dispatch(userLoaded(user));
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // A 401 here means the session is gone; retrying cannot fix it and only
      // delays the redirect.
      if (error instanceof ApiError && error.isAuthFailure) return false;
      return failureCount < 2;
    },
  });
}

export function useLogin() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: async (result) => {
      // The response is a union. An MFA challenge is a successful request that
      // is not a completed login, so nothing is stored and no redirect happens.
      if (!('accessToken' in result)) return;

      const token = getAccessToken();
      if (token) dispatch(sessionStarted({ user: result.user, accessToken: token }));

      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
      await navigate(ROUTES.customers, { replace: true });
    },
  });
}

export function useLogout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: async () => {
      /*
       * onSettled, not onSuccess: if the request fails the user still expects
       * to be signed out locally, and the token has already been discarded.
       */
      dispatch(sessionEnded());
      queryClient.clear();
      await navigate(ROUTES.login, { replace: true });
    },
  });
}
